// src/components/billing/AdminBillingPage.jsx
//
// Internal "Billing" page (/admin/billing): every invoice across all
// clients with its payment status, fleet-wide outstanding/overdue, and the
// card payments taken in the portal - with anything that didn't reach Xero
// flagged so it can be retried or marked as recorded by hand.
//
// Xero itself is reached only through Nautilus (it owns the Xero sign-in for
// both companies), so there's no "Connect Xero" here: the strip at the top
// shows what Nautilus reports, and reconnecting happens in Nautilus.

import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetchJSON } from '../../api/apiClient';
import FleetHeader from '../FleetHeader';
import InvoiceDrawer from './InvoiceDrawer';
import { StatusBadge, formatMoney, formatDate, formatDateTime, LoadingScreen } from './billingShared';
import '../../styles/fleet-theme.css';
import './Billing.css';

const PdfPreview = lazy(() => import('./PdfPreview'));

const PAGE = 50;
const STATUS_FILTERS = [
  { id: 'unpaid', label: 'Unpaid' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'paid', label: 'Paid' },
  { id: 'draft', label: 'Draft' },
  { id: 'void', label: 'Void' },
  { id: '', label: 'All' },
];
const XERO_LABEL = {
  pending: 'Waiting', recording: 'Posting…', recorded: 'In Xero', held: 'Needs a person', failed: 'Retrying', skipped: 'By hand',
};

export default function AdminBillingPage({ user, onLogout }) {
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState('invoices');
  const [status, setStatus] = useState('unpaid');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [clientId, setClientId] = useState('');
  const [bu, setBu] = useState('');
  const [portalOnly, setPortalOnly] = useState(true);
  const [clients, setClients] = useState([]);
  const [list, setList] = useState({ invoices: [], total: 0 });
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [payments, setPayments] = useState(null);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(null);
  const [previewInv, setPreviewInv] = useState(null);
  const reqId = useRef(0);

  const loadSummary = useCallback(async () => {
    try { setSummary(await apiFetchJSON('/api/v2/admin/billing/summary')); }
    catch (e) { setSummary({ error: e.message }); }
  }, []);

  useEffect(() => {
    loadSummary();
    apiFetchJSON('/api/v2/admin/sites/clients').then((r) => setClients(r.clients || [])).catch(() => {});
  }, [loadSummary]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const loadList = useCallback(async (offset = 0) => {
    const id = ++reqId.current;
    setListLoading(true);
    setListError(null);
    const p = new URLSearchParams({ limit: PAGE, offset });
    if (status) p.set('status', status);
    if (debounced) p.set('q', debounced);
    if (clientId) p.set('client_id', clientId);
    if (bu) p.set('bu', bu);
    p.set('portal_only', portalOnly && !clientId ? 'true' : 'false');
    try {
      const r = await apiFetchJSON(`/api/v2/admin/billing/invoices?${p}`);
      if (id !== reqId.current) return;
      setList((prev) => offset ? { invoices: [...prev.invoices, ...r.invoices], total: r.total } : r);
    } catch (e) {
      if (id === reqId.current) setListError(e.message);
    } finally {
      if (id === reqId.current) setListLoading(false);
    }
  }, [status, debounced, clientId, bu, portalOnly]);

  useEffect(() => { if (tab === 'invoices') loadList(0); }, [loadList, tab]);

  const loadPayments = useCallback(async () => {
    try {
      setPayments(await apiFetchJSON(`/api/v2/admin/billing/payments?problems=${problemsOnly}`));
    } catch (e) {
      setPayments({ error: e.message, payments: [] });
    }
  }, [problemsOnly]);

  useEffect(() => { if (tab === 'payments') loadPayments(); }, [tab, loadPayments]);

  const clientName = useMemo(() => {
    const m = {};
    clients.forEach((c) => { m[c.client_id] = c.client_name; });
    return m;
  }, [clients]);

  const paymentAction = async (pi, action) => {
    let body;
    if (action === 'mark-recorded') {
      const note = window.prompt('You recorded this payment in Xero by hand? Add a note (optional):', '');
      if (note === null) return;
      body = JSON.stringify({ note });
    }
    setBusy(pi);
    try {
      const r = await apiFetchJSON(`/api/v2/admin/billing/payments/${pi}/${action}`, { method: 'POST', body });
      if (r.xero_status === 'failed') setBanner({ ok: false, text: `Still not in Xero: ${r.xero_error}` });
      await Promise.all([loadPayments(), loadSummary()]);
    } catch (e) {
      setBanner({ ok: false, text: e.message });
    } finally {
      setBusy(null);
    }
  };

  if (!summary) return <LoadingScreen text="Loading billing…" />;

  const companies = Object.entries(summary.companies || {});
  const byCur = (summary.by_currency || []).filter((b) => b.outstanding > 0);
  const unrecorded = summary.unrecorded_payments || 0;

  return (
    <div>
      <FleetHeader
        title="Billing"
        user={user}
        onLogout={onLogout}
        searchValue={query}
        onSearchChange={(v) => { setQuery(v); setTab('invoices'); }}
        searchPlaceholder="search invoice number, vessel, reference"
      />
      <div className="sd-page bl-page bl-page-admin">

        {/* What Nautilus reports, per company */}
        <div className="bl-integrations">
          {summary.bridge_error && (
            <div className="bl-integration"><span className="bl-dot bad"></span><span>Nautilus: {summary.bridge_error}</span></div>
          )}
          {companies.map(([key, c]) => (
            <div className="bl-integration" key={key} title={c.token_problem || ''}>
              <span className={`bl-dot ${c.connected && !c.token_problem ? 'ok' : 'bad'}`}></span>
              <span className="bl-integration-name">{c.label}</span>
              <span>{c.connected ? (c.token_problem ? 'Xero needs reconnecting in Nautilus' : 'Xero connected') : 'Xero not connected'}</span>
              <span className="bl-muted">·</span>
              <span>Card payments {c.enabled ? 'on' : 'off'}{c.enabled && c.surcharge_percent ? ` (+${c.surcharge_percent}% card fee)` : ''}</span>
              {c.enabled && (
                <>
                  <span className="bl-muted">·</span>
                  <span className={c.can_record_payments ? '' : 'bl-warn-text'}>
                    {c.can_record_payments
                      ? 'recorded in Xero automatically'
                      : c.posting_paused ? 'Xero posting paused in Nautilus' : 'recorded in Xero by hand'}
                  </span>
                </>
              )}
              {!c.visible_to_clients && <span className="bl-muted">· hidden from clients</span>}
            </div>
          ))}
          <div className="bl-integration bl-integration-muted">
            Client roles with Billing: {(summary.client_roles || []).join(', ')}
            {summary.mirror_fetched_at && ` · Xero copy from ${formatDateTime(summary.mirror_fetched_at)}`}
          </div>
        </div>

        {banner && (
          <div className={banner.ok ? 'ft-toolbar-success bl-banner' : 'ft-toolbar-error bl-banner'} role="status">
            {banner.text}
            <button type="button" className="bl-icon-btn" onClick={() => setBanner(null)} aria-label="Dismiss"><i className="fas fa-times"></i></button>
          </div>
        )}
        {summary.error && <div className="ft-toolbar-error">Billing database: {summary.error}</div>}

        <div className="ft-stat-row bl-stats">
          <div className="ft-stat-card">
            <div className="ft-stat-label">Outstanding</div>
            {byCur.length === 0 ? <div className="ft-stat-value ft-value-secondary">0</div> : byCur.slice(0, 3).map((b) => (
              <div key={b.currency} className="ft-stat-value bl-stat-money">{formatMoney(b.outstanding, b.currency)}</div>
            ))}
            <div className="ft-stat-note">{summary.unpaid_count || 0} unpaid invoices</div>
          </div>
          <button type="button" className={`ft-stat-card bl-stat-btn ${summary.overdue_count ? 'ft-alert' : ''} ${status === 'overdue' && tab === 'invoices' ? 'ft-stat-active' : ''}`}
                  onClick={() => { setTab('invoices'); setStatus('overdue'); }}>
            <div className="ft-stat-label">Overdue</div>
            <div className={`ft-stat-value ${summary.overdue_count ? 'ft-value-accent' : ''}`}>{summary.overdue_count || 0}</div>
            <div className="ft-stat-note">
              {byCur.filter((b) => b.overdue > 0).map((b) => formatMoney(b.overdue, b.currency)).join(' + ') || 'nothing late'}
            </div>
          </button>
          <button type="button" className={`ft-stat-card bl-stat-btn ${summary.hidden_unpaid_count ? 'ft-alert' : ''} ${!portalOnly && tab === 'invoices' ? 'ft-stat-active' : ''}`}
                  onClick={() => { setTab('invoices'); setStatus('unpaid'); setPortalOnly(false); }}>
            <div className="ft-stat-label">Unpaid, no client login</div>
            <div className={`ft-stat-value ${summary.hidden_unpaid_count ? 'ft-value-accent' : ''}`}>{summary.hidden_unpaid_count || 0}</div>
            <div className="ft-stat-note">yacht has no linked client acc</div>
          </button>
          <button type="button" className={`ft-stat-card bl-stat-btn ${unrecorded ? 'ft-alert' : ''} ${tab === 'payments' && problemsOnly ? 'ft-stat-active' : ''}`}
                  onClick={() => { setTab('payments'); setProblemsOnly(true); }}>
            <div className="ft-stat-label">Card payments not in Xero</div>
            <div className={`ft-stat-value ${unrecorded ? 'ft-value-accent' : 'ft-value-secondary'}`}>{unrecorded}</div>
            <div className="ft-stat-note">{unrecorded ? 'needs a look' : 'all recorded'}</div>
          </button>
        </div>

        <div className="ft-filter-row bl-filters">
          <div className="bl-tabs" role="tablist">
            <button role="tab" aria-selected={tab === 'invoices'} className={`bl-tab ${tab === 'invoices' ? 'active' : ''}`} onClick={() => setTab('invoices')}>Invoices</button>
            <button role="tab" aria-selected={tab === 'payments'} className={`bl-tab ${tab === 'payments' ? 'active' : ''}`} onClick={() => setTab('payments')}>Card payments</button>
          </div>
          {tab === 'invoices' ? (
            <div className="bl-filter-group">
              <div className="bl-status-pills">
                {STATUS_FILTERS.map((f) => (
                  <button key={f.id || 'all'} className={'ft-pill' + (status === f.id ? ' active' : '')} onClick={() => setStatus(f.id)}>{f.label}</button>
                ))}
              </div>
              <select className="bl-select bl-status-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
                {STATUS_FILTERS.map((f) => <option key={f.id || 'all'} value={f.id}>{f.label}</option>)}
              </select>
              <select className="bl-select" value={bu} onChange={(e) => setBu(e.target.value)} aria-label="Filter by company">
                <option value="">Both companies</option>
                <option value="247">Twenty4Seven</option>
                <option value="netdream">NetDream Systems</option>
              </select>
              <label className="bl-check">
                <input type="checkbox" checked={portalOnly} onChange={(e) => setPortalOnly(e.target.checked)} disabled={!!clientId} />
                Client-visible only
              </label>
              <select className="bl-select" value={clientId} onChange={(e) => setClientId(e.target.value)} aria-label="Filter by client">
                <option value="">All clients</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.client_name}</option>)}
              </select>
            </div>
          ) : (
            <div className="bl-filter-group">
              <button className={'ft-pill' + (!problemsOnly ? ' active' : '')} onClick={() => setProblemsOnly(false)}>All</button>
              <button className={'ft-pill' + (problemsOnly ? ' active' : '')} onClick={() => setProblemsOnly(true)}>Not in Xero</button>
            </div>
          )}
        </div>

        {tab === 'invoices' && (
          <>
            {listError && <div className="ft-toolbar-error">{listError}</div>}
            <div className="bl-table-wrap bl-table-wrap-admin">
              <table className="ft-table bl-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Client</th>
                    <th>Vessel</th>
                    <th>Issued</th>
                    <th>Due</th>
                    <th className="ft-num">Total</th>
                    <th className="ft-num">Balance</th>
                    <th>Status</th>
                    <th aria-label="PDF"></th>
                  </tr>
                </thead>
                <tbody>
                  {list.invoices.map((inv) => (
                    <tr key={inv.id} className="bl-row" onClick={() => setOpenId(inv.id)} tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setOpenId(inv.id)}>
                      <td className="ft-mono">{inv.invoice_number}<div className="bl-small bl-muted">{inv.business_unit === 'netdream' ? 'NDS' : '247'}</div></td>
                      <td>{inv.client_name || <span className="bl-muted">No client</span>}</td>
                      <td>{inv.site_name || <span className="bl-muted" title="Not on a portal site">{inv.yacht_name || inv.contact_name || '—'}</span>}</td>
                      <td>{formatDate(inv.issue_date)}</td>
                      <td className={inv.status === 'overdue' ? 'bl-overdue-text' : ''}>{formatDate(inv.due_date)}</td>
                      <td className="ft-num">{formatMoney(inv.total, inv.currency)}</td>
                      <td className="ft-num">{inv.status === 'paid' || inv.status === 'void' ? '—' : formatMoney(inv.amount_due, inv.currency)}</td>
                      <td><StatusBadge status={inv.status} daysOverdue={inv.days_overdue} /></td>
                      <td className="bl-actions-cell">
                        <button type="button" className="bl-btn-ghost" onClick={(e) => { e.stopPropagation(); setPreviewInv(inv); }} aria-label={`View PDF of ${inv.invoice_number}`} title="View PDF">
                          <i className="fas fa-file-pdf"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Phones: compact cards instead of the wide table */}
            <div className="bl-cards bl-cards-admin">
              {list.invoices.map((inv) => (
                <div key={inv.id} className={`bl-acard ${inv.status === 'overdue' ? 'is-overdue' : ''}`}
                     onClick={() => setOpenId(inv.id)} role="button" tabIndex={0}
                     onKeyDown={(e) => e.key === 'Enter' && setOpenId(inv.id)}>
                  <div className="bl-acard-row">
                    <span className="bl-acard-id">
                      <span className="ft-mono">{inv.invoice_number}</span>
                      <span className="bl-acard-tag">{inv.business_unit === 'netdream' ? 'NDS' : '247'}</span>
                    </span>
                    <StatusBadge status={inv.status} daysOverdue={inv.days_overdue} />
                  </div>
                  <div className="bl-acard-who">
                    <span className={inv.client_name ? '' : 'bl-muted'}>{inv.client_name || 'No client'}</span>
                    <span className="bl-muted"> · </span>
                    <span>{inv.site_name || inv.yacht_name || inv.contact_name || '—'}</span>
                  </div>
                  <div className="bl-acard-row bl-acard-foot">
                    <span className={`bl-small ${inv.status === 'overdue' ? 'bl-overdue-text' : 'bl-muted'}`}>
                      {inv.status === 'paid' || inv.status === 'void' ? `Issued ${formatDate(inv.issue_date)}` : `Due ${formatDate(inv.due_date)}`}
                    </span>
                    <span className="bl-acard-amount ft-num">
                      {formatMoney(inv.status === 'paid' || inv.status === 'void' ? inv.total : inv.amount_due, inv.currency)}
                    </span>
                    <button type="button" className="bl-btn-ghost bl-acard-pdf" onClick={(e) => { e.stopPropagation(); setPreviewInv(inv); }} aria-label={`View PDF of ${inv.invoice_number}`}>
                      <i className="fas fa-file-pdf"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {!listLoading && list.invoices.length === 0 && !listError && (
              <div className="bl-empty">No invoices match these filters.</div>
            )}
            <div className="bl-list-foot">
              <span>{list.invoices.length} of {list.total}</span>
              {list.invoices.length < list.total && (
                <button className="bl-btn-ghost" disabled={listLoading} onClick={() => loadList(list.invoices.length)}>
                  {listLoading ? 'Loading…' : 'Load more'}
                </button>
              )}
              {listLoading && list.invoices.length === 0 && <span>Loading…</span>}
            </div>
          </>
        )}

        {tab === 'payments' && (
          <div className="bl-table-wrap bl-table-wrap-admin">
            {payments?.error && <div className="ft-toolbar-error">{payments.error}</div>}
            <table className="ft-table bl-table">
              <thead>
                <tr>
                  <th>Paid</th>
                  <th>Invoice</th>
                  <th>Client</th>
                  <th className="ft-num">Amount</th>
                  <th>Paid by</th>
                  <th>Stripe</th>
                  <th>Xero</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {(payments?.payments || []).map((p) => (
                  <tr key={p.payment_intent_id}>
                    <td>{formatDateTime(p.paid_at)}</td>
                    <td className="ft-mono bl-link" onClick={() => setOpenId(p.invoice_id)}>{p.invoice_number}<div className="bl-small bl-muted">{p.business_unit === 'netdream' ? 'NDS' : '247'}</div></td>
                    <td>{clientName[p.client_id] || p.client_id}</td>
                    <td className="ft-num">
                      {formatMoney(p.amount + (p.surcharge || 0), p.currency)}
                      {p.surcharge > 0 && <div className="bl-small bl-muted">incl. {formatMoney(p.surcharge, p.currency)} card fee</div>}
                    </td>
                    <td className="bl-small">{p.username}</td>
                    <td>
                      <a className="ft-mono bl-small" href={`https://dashboard.stripe.com/payments/${p.payment_intent_id}`} target="_blank" rel="noreferrer">
                        {p.payment_intent_id.slice(0, 14)}…
                      </a>
                    </td>
                    <td>
                      <span className={`ft-badge ${p.xero_status === 'recorded' || p.xero_status === 'skipped' ? 'ft-badge-online' : 'ft-badge-down'}`} title={p.xero_error || ''}>
                        {XERO_LABEL[p.xero_status] || p.xero_status}
                      </span>
                      {p.xero_error && <div className="bl-small bl-error-text bl-clamp">{p.xero_error}</div>}
                    </td>
                    <td className="bl-actions-cell">
                      {['failed', 'pending', 'held'].includes(p.xero_status) && (
                        <>
                          <button className="bl-btn-ghost" disabled={busy === p.payment_intent_id} onClick={() => paymentAction(p.payment_intent_id, 'retry')}>Retry</button>
                          <button className="bl-btn-ghost" disabled={busy === p.payment_intent_id} onClick={() => paymentAction(p.payment_intent_id, 'mark-recorded')}>Mark recorded</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="bl-cards bl-cards-admin">
              {(payments?.payments || []).map((p) => (
                <div key={p.payment_intent_id} className={`bl-acard ${['recorded', 'skipped'].includes(p.xero_status) ? '' : 'is-overdue'}`}>
                  <div className="bl-acard-row">
                    <span className="bl-acard-id bl-link" onClick={() => setOpenId(p.invoice_id)}>
                      <span className="ft-mono">{p.invoice_number}</span>
                      <span className="bl-acard-tag">{p.business_unit === 'netdream' ? 'NDS' : '247'}</span>
                    </span>
                    <span className={`ft-badge ${p.xero_status === 'recorded' || p.xero_status === 'skipped' ? 'ft-badge-online' : 'ft-badge-down'}`}>
                      {XERO_LABEL[p.xero_status] || p.xero_status}
                    </span>
                  </div>
                  <div className="bl-acard-who">
                    <span>{clientName[p.client_id] || p.client_id}</span>
                    <span className="bl-muted"> · {p.username}</span>
                  </div>
                  <div className="bl-acard-row bl-acard-foot">
                    <span className="bl-small bl-muted">{formatDateTime(p.paid_at)}</span>
                    <span className="bl-acard-amount ft-num">{formatMoney(p.amount + (p.surcharge || 0), p.currency)}</span>
                  </div>
                  {p.xero_error && <div className="bl-small bl-error-text">{p.xero_error}</div>}
                  {['failed', 'pending', 'held'].includes(p.xero_status) && (
                    <div className="bl-acard-actions">
                      <button className="bl-btn-ghost" disabled={busy === p.payment_intent_id} onClick={() => paymentAction(p.payment_intent_id, 'retry')}>Retry</button>
                      <button className="bl-btn-ghost" disabled={busy === p.payment_intent_id} onClick={() => paymentAction(p.payment_intent_id, 'mark-recorded')}>Mark recorded</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {payments && !payments.error && payments.payments.length === 0 && (
              <div className="bl-empty">{problemsOnly ? 'Every card payment is recorded in Xero.' : 'No card payments yet.'}</div>
            )}
          </div>
        )}
      </div>

      {previewInv && (
        <Suspense fallback={null}>
          <PdfPreview
            url={`/api/v2/admin/billing/invoices/${encodeURIComponent(previewInv.id)}/pdf`}
            invoiceNumber={previewInv.invoice_number}
            onClose={() => setPreviewInv(null)}
          />
        </Suspense>
      )}

      {openId && (
        <InvoiceDrawer
          scope="admin"
          invoiceId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => { loadSummary(); if (tab === 'payments') loadPayments(); }}
        />
      )}
    </div>
  );
}
